/**
 * Bibliothèque de mires de test standards pour VisualGrid
 * OPTIMISÉ: Utilise ImageData pour les gradients (100x+ plus rapide que fillRect)
 */

/**
 * Crée un gradient horizontal optimisé avec ImageData
 * PERF: 1920x1080 en ~2ms au lieu de 500ms+ avec fillRect
 */
function createHorizontalGradient(
  width: number,
  height: number,
  colorFunc: (step: number) => [r: number, g: number, b: number]
): ImageData {
  const imageData = new ImageData(width, height);
  const data = imageData.data;

  for (let x = 0; x < width; x++) {
    const [r, g, b] = colorFunc(x);
    for (let y = 0; y < height; y++) {
      const idx = (y * width + x) * 4;
      data[idx] = r;
      data[idx + 1] = g;
      data[idx + 2] = b;
      data[idx + 3] = 255;
    }
  }

  return imageData;
}

/**
 * Crée un gradient vertical optimisé avec ImageData
 */
function createVerticalGradient(
  width: number,
  height: number,
  colorFunc: (step: number) => [r: number, g: number, b: number]
): ImageData {
  const imageData = new ImageData(width, height);
  const data = imageData.data;

  for (let y = 0; y < height; y++) {
    const [r, g, b] = colorFunc(y);
    for (let x = 0; x < width; x++) {
      const idx = (y * width + x) * 4;
      data[idx] = r;
      data[idx + 1] = g;
      data[idx + 2] = b;
      data[idx + 3] = 255;
    }
  }

  return imageData;
}

/**
 * Dessine une mire de test standard sur un canvas
 * @param ctx Contexte 2D du canvas
 * @param width Largeur du canvas
 * @param height Hauteur du canvas
 * @param time Temps écoulé (pour les animations)
 * @param patternType Type de mire à dessiner
 * @param options Options supplémentaires pour les mires
 */
export function drawStandardPattern(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  time: number,
  patternType: string,
  options: { 
    direction?: 'horizontal' | 'vertical' | 'diagonal',
    gridSize?: number,
    animationSpeed?: number
  } = {}
): void {
  // Valeurs par défaut pour les options
  const direction = options.direction || 'horizontal';
  const gridSize = options.gridSize || 192;
  const animationSpeed = options.animationSpeed || 1;
  
  // Effacer le canvas
  ctx.clearRect(0, 0, width, height);
  
  switch (patternType) {
    case "liveVHScroll": {
      // Live V H Scroll - Défilement vertical et horizontal de lignes
      const lineWidth = 2;
      const scrollSpeed = time * 0.1 * animationSpeed;
      const vScrollPosition = (scrollSpeed % height);
      const hScrollPosition = (scrollSpeed % width);
      
      // Fond noir
      ctx.fillStyle = "#000000";
      ctx.fillRect(0, 0, width, height);
      
      // Ligne blanche qui défile verticalement
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, vScrollPosition, width, lineWidth);
      
      // Ligne blanche qui défile horizontalement
      ctx.fillRect(hScrollPosition, 0, lineWidth, height);
      break;
    }
    
    case "liveVScroll": {
      // Live V Scroll - Défilement vertical d'une ligne
      const lineHeight = 2;
      const scrollSpeed = time * 0.1 * animationSpeed;
      const scrollPosition = (scrollSpeed % height);
      
      // Fond noir
      ctx.fillStyle = "#000000";
      ctx.fillRect(0, 0, width, height);
      
      // Ligne blanche qui défile verticalement
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, scrollPosition, width, lineHeight);
      break;
    }
    
    case "lineHScroll": {
      // Line H Scroll - Défilement horizontal d'une ligne
      const lineWidth = 2;
      const scrollSpeed = time * 0.1 * animationSpeed;
      const scrollPosition = (scrollSpeed % width);
      
      // Fond noir
      ctx.fillStyle = "#000000";
      ctx.fillRect(0, 0, width, height);
      
      // Ligne blanche qui défile horizontalement
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(scrollPosition, 0, lineWidth, height);
      break;
    }
    
    case "grayRamp": {
      // Gray Ramp - Dégradé de gris (OPTIMISÉ avec ImageData)
      if (direction === 'horizontal') {
        const imageData = createHorizontalGradient(width, height, (x) => {
          const gray = Math.floor(255 * (x / width));
          return [gray, gray, gray];
        });
        ctx.putImageData(imageData, 0, 0);
      } else {
        const imageData = createVerticalGradient(width, height, (y) => {
          const gray = Math.floor(255 * (y / height));
          return [gray, gray, gray];
        });
        ctx.putImageData(imageData, 0, 0);
      }
      break;
    }
    
    case "greenRamp": {
      // Green Ramp - Dégradé de vert (OPTIMISÉ)
      if (direction === 'horizontal') {
        const imageData = createHorizontalGradient(width, height, (x) => {
          const green = Math.floor(255 * (x / width));
          return [0, green, 0];
        });
        ctx.putImageData(imageData, 0, 0);
      } else {
        const imageData = createVerticalGradient(width, height, (y) => {
          const green = Math.floor(255 * (y / height));
          return [0, green, 0];
        });
        ctx.putImageData(imageData, 0, 0);
      }
      break;
    }

    case "redRamp": {
      // Red Ramp - Dégradé de rouge (OPTIMISÉ)
      if (direction === 'horizontal') {
        const imageData = createHorizontalGradient(width, height, (x) => {
          const red = Math.floor(255 * (x / width));
          return [red, 0, 0];
        });
        ctx.putImageData(imageData, 0, 0);
      } else {
        const imageData = createVerticalGradient(width, height, (y) => {
          const red = Math.floor(255 * (y / height));
          return [red, 0, 0];
        });
        ctx.putImageData(imageData, 0, 0);
      }
      break;
    }

    case "blueRamp": {
      // Blue Ramp - Dégradé de bleu (OPTIMISÉ)
      if (direction === 'horizontal') {
        const imageData = createHorizontalGradient(width, height, (x) => {
          const blue = Math.floor(255 * (x / width));
          return [0, 0, blue];
        });
        ctx.putImageData(imageData, 0, 0);
      } else {
        const imageData = createVerticalGradient(width, height, (y) => {
          const blue = Math.floor(255 * (y / height));
          return [0, 0, blue];
        });
        ctx.putImageData(imageData, 0, 0);
      }
      break;
    }

    case "whiteRamp": {
      // White Ramp - Dégradé de blanc (OPTIMISÉ)
      if (direction === 'horizontal') {
        const imageData = createHorizontalGradient(width, height, (x) => {
          const white = Math.floor(255 * (x / width));
          return [white, white, white];
        });
        ctx.putImageData(imageData, 0, 0);
      } else {
        const imageData = createVerticalGradient(width, height, (y) => {
          const white = Math.floor(255 * (y / height));
          return [white, white, white];
        });
        ctx.putImageData(imageData, 0, 0);
      }
      break;
    }
    
    case "movingBall": {
      // Moving Ball - Balle blanche sur fond noir qui rebondit sur les parois
      const radius = Math.min(width, height) * 0.05;
      
      // Utiliser le temps pour calculer la position de la balle
      const speed = 0.2 * animationSpeed; // Vitesse de déplacement
      
      // Calculer les positions x et y avec rebond
      const bounceX = ((time * speed) % (2 * width)) / width;
      const bounceY = ((time * speed * 0.7) % (2 * height)) / height;
      
      // Position avec rebond
      // Ajuster pour que la balle rebondisse quand elle touche les parois et non son centre
      let centerX = bounceX <= 1 ? bounceX * width : (2 - bounceX) * width;
      let centerY = bounceY <= 1 ? bounceY * height : (2 - bounceY) * height;
      
      // Limiter les positions pour que la balle ne dépasse pas les bords
      centerX = Math.max(radius, Math.min(width - radius, centerX));
      centerY = Math.max(radius, Math.min(height - radius, centerY));
      
      // Fond noir
      ctx.fillStyle = "#000000";
      ctx.fillRect(0, 0, width, height);
      
      // Balle blanche
      ctx.fillStyle = "#FFFFFF";
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    
    case "colorBar": {
      // 100/75/50/25% Color Bar (OPTIMISÉ)
      const barWidth = width / 4;
      const colors = [
        [[255, 0, 0], [191, 0, 0], [127, 0, 0], [63, 0, 0]],     // Rouge
        [[0, 255, 0], [0, 191, 0], [0, 127, 0], [0, 63, 0]],     // Vert
        [[0, 0, 255], [0, 0, 191], [0, 0, 127], [0, 0, 63]],     // Bleu
        [[255, 255, 255], [191, 191, 191], [127, 127, 127], [63, 63, 63]]  // Blanc
      ];

      const imageData = new ImageData(width, height);
      const data = imageData.data;

      for (let colorIndex = 0; colorIndex < colors.length; colorIndex++) {
        const colorSet = colors[colorIndex];
        const colorBarHeight = height / colors.length;
        const y = Math.floor(colorIndex * colorBarHeight);
        const nextY = Math.floor((colorIndex + 1) * colorBarHeight);

        for (let intensityIndex = 0; intensityIndex < 4; intensityIndex++) {
          const x = Math.floor(intensityIndex * barWidth);
          const nextX = Math.floor((intensityIndex + 1) * barWidth);
          const [r, g, b] = colorSet[intensityIndex];

          for (let py = y; py < nextY; py++) {
            for (let px = x; px < nextX; px++) {
              const idx = (py * width + px) * 4;
              data[idx] = r;
              data[idx + 1] = g;
              data[idx + 2] = b;
              data[idx + 3] = 255;
            }
          }
        }
      }

      ctx.putImageData(imageData, 0, 0);
      break;
    }
    
    case "grayBar8": {
      // 8 Step Grey Bar (OPTIMISÉ)
      const steps = 8;
      const barWidth = width / steps;
      const imageData = new ImageData(width, height);
      const data = imageData.data;

      for (let i = 0; i < steps; i++) {
        const gray = Math.floor(255 * (i / (steps - 1)));
        const x = Math.floor(i * barWidth);
        const nextX = Math.floor((i + 1) * barWidth);

        for (let py = 0; py < height; py++) {
          for (let px = x; px < nextX; px++) {
            const idx = (py * width + px) * 4;
            data[idx] = gray;
            data[idx + 1] = gray;
            data[idx + 2] = gray;
            data[idx + 3] = 255;
          }
        }
      }

      ctx.putImageData(imageData, 0, 0);
      break;
    }

    case "grayBar16": {
      // 16 Step Grey Bar (OPTIMISÉ)
      const steps = 16;
      const barWidth = width / steps;
      const imageData = new ImageData(width, height);
      const data = imageData.data;

      for (let i = 0; i < steps; i++) {
        const gray = Math.floor(255 * (i / (steps - 1)));
        const x = Math.floor(i * barWidth);
        const nextX = Math.floor((i + 1) * barWidth);

        for (let py = 0; py < height; py++) {
          for (let px = x; px < nextX; px++) {
            const idx = (py * width + px) * 4;
            data[idx] = gray;
            data[idx + 1] = gray;
            data[idx + 2] = gray;
            data[idx + 3] = 255;
          }
        }
      }

      ctx.putImageData(imageData, 0, 0);
      break;
    }
    
    case "redScreen": {
      // Red Screen - Écran rouge uni
      ctx.fillStyle = "#FF0000";
      ctx.fillRect(0, 0, width, height);
      break;
    }
    
    case "blueScreen": {
      // Blue Screen - Écran bleu uni
      ctx.fillStyle = "#0000FF";
      ctx.fillRect(0, 0, width, height);
      break;
    }
    
    case "greenScreen": {
      // Green Screen - Écran vert uni
      ctx.fillStyle = "#00FF00";
      ctx.fillRect(0, 0, width, height);
      break;
    }
    
    case "cyanScreen": {
      // Cyan Screen - Écran cyan uni
      ctx.fillStyle = "#00FFFF";
      ctx.fillRect(0, 0, width, height);
      break;
    }
    
    case "yellowScreen": {
      // Yellow Screen - Écran jaune uni
      ctx.fillStyle = "#FFFF00";
      ctx.fillRect(0, 0, width, height);
      break;
    }
    
    case "blackScreen": {
      // Black Screen - Écran noir uni
      ctx.fillStyle = "#000000";
      ctx.fillRect(0, 0, width, height);
      break;
    }
    
    case "whiteScreen": {
      // White Screen - Écran blanc uni
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, width, height);
      break;
    }
    
    case "verticalSplit": {
      // Vertical Split - Séparation verticale noir/blanc
      ctx.fillStyle = "#000000";
      ctx.fillRect(0, 0, width / 2, height);
      
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(width / 2, 0, width / 2, height);
      break;
    }
    
    case "horizontalSplit": {
      // Horizontal Split - Séparation horizontale noir/blanc
      ctx.fillStyle = "#000000";
      ctx.fillRect(0, 0, width, height / 2);
      
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, height / 2, width, height / 2);
      break;
    }
    
    case "quarterBlock": {
      // QuarterBlock 1and2 checkboard - Damier en quarts
      ctx.fillStyle = "#000000";
      ctx.fillRect(0, 0, width, height);
      
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, width / 2, height / 2); // Quart supérieur gauche
      ctx.fillRect(width / 2, height / 2, width / 2, height / 2); // Quart inférieur droit
      break;
    }
    
    case "alternateCheckboard": {
      // Alternate B&W checkboard - Damier noir et blanc alterné
      // Utiliser la taille de grille fournie ou une valeur par défaut
      const cellSize = gridSize || Math.min(width, height) / 8;
      
      // Simplifier l'animation du damier
      // Calculer l'état du damier en fonction du temps
      const cycleTime = 1000; // 1 seconde par défaut
      const cycleProgress = (time * animationSpeed) % (cycleTime * 2); // Cycle complet aller-retour
      const isInverted = cycleProgress >= cycleTime; // Inverser quand on dépasse la moitié du cycle
      
      for (let y = 0; y < height; y += cellSize) {
        for (let x = 0; x < width; x += cellSize) {
          const isEven = (Math.floor(x / cellSize) + Math.floor(y / cellSize)) % 2 === 0;
          
          // Inverser les couleurs en fonction de l'état
          ctx.fillStyle = (isEven !== isInverted) ? "#FFFFFF" : "#000000";
          ctx.fillRect(x, y, cellSize, cellSize);
        }
      }
      break;
    }
    
    case "smpteBar": {
      // SMPTE Color Bars
      const barWidth = width / 7;
      const barHeight = height * 2/3;
      const bottomHeight = height / 3;
      
      // Barres de couleur principales (75%)
      const colors = [
        "#FFFFFF", // Blanc
        "#FFFF00", // Jaune
        "#00FFFF", // Cyan
        "#00FF00", // Vert
        "#FF00FF", // Magenta
        "#FF0000", // Rouge
        "#0000FF"  // Bleu
      ];
      
      // Dessiner les barres principales
      for (let i = 0; i < 7; i++) {
        ctx.fillStyle = colors[i];
        ctx.fillRect(i * barWidth, 0, barWidth, barHeight);
      }
      
      // Barres inférieures
      const bottomColors = [
        "#0000FF", // Bleu
        "#000000", // Noir
        "#FF00FF", // Magenta
        "#000000", // Noir
        "#00FFFF", // Cyan
        "#000000", // Noir
        "#FFFFFF"  // Blanc
      ];
      
      // Dessiner les barres inférieures
      for (let i = 0; i < 7; i++) {
        ctx.fillStyle = bottomColors[i];
        ctx.fillRect(i * barWidth, barHeight, barWidth, bottomHeight);
      }
      
      // Plage (Pluge) - Rectangle noir central avec barres de référence
      const plugeWidth = barWidth * 4;
      const plugeHeight = bottomHeight / 2;
      const plugeX = barWidth * 1.5;
      const plugeY = barHeight + bottomHeight / 2;
      
      ctx.fillStyle = "#000000";
      ctx.fillRect(plugeX, plugeY, plugeWidth, plugeHeight);
      
      // Barres de référence dans la plage
      const refBarWidth = barWidth * 0.8;
      const refBarHeight = plugeHeight * 0.8;
      const refBarY = plugeY + (plugeHeight - refBarHeight) / 2;
      
      // Barre de référence gris foncé (3.5 IRE)
      ctx.fillStyle = "#101010";
      ctx.fillRect(plugeX + refBarWidth * 0.2, refBarY, refBarWidth, refBarHeight);
      
      // Barre de référence noir (7.5 IRE)
      ctx.fillStyle = "#202020";
      ctx.fillRect(plugeX + refBarWidth * 1.2, refBarY, refBarWidth, refBarHeight);
      
      // Barre de référence gris clair (11.5 IRE)
      ctx.fillStyle = "#303030";
      ctx.fillRect(plugeX + refBarWidth * 2.2, refBarY, refBarWidth, refBarHeight);
      break;
    }
    
    // ========== MIRES SPÉCIFIQUES VIDÉOPROJECTEURS ==========
    
    case "pluge": {
      // PLUGE (Picture Line-Up Generation Equipment) - Test niveau de noir
      ctx.fillStyle = "#000000";
      ctx.fillRect(0, 0, width, height);
      
      const centerX = width / 2;
      const centerY = height / 2;
      const rectWidth = width * 0.6;
      const rectHeight = height * 0.4;
      const startX = centerX - rectWidth / 2;
      const startY = centerY - rectHeight / 2;
      
      // Zone de test PLUGE avec différents niveaux de noir
      const sectionWidth = rectWidth / 5;
      
      // Niveaux IRE : -4%, 0%, 2%, 4%, 8%
      const levels = ["#000000", "#080808", "#0F0F0F", "#171717", "#2F2F2F"];
      const labels = ["-4%", "0%", "2%", "4%", "8%"];
      
      for (let i = 0; i < 5; i++) {
        ctx.fillStyle = levels[i];
        ctx.fillRect(startX + i * sectionWidth, startY, sectionWidth, rectHeight);
        
        // Étiquettes
        ctx.fillStyle = "#FFFFFF";
        ctx.font = `${Math.max(12, width / 80)}px Arial`;
        ctx.textAlign = "center";
        ctx.fillText(labels[i], startX + i * sectionWidth + sectionWidth / 2, startY + rectHeight + 30);
      }
      break;
    }
    
    case "zonePlate": {
      // Zone Plate - Test de convergence et netteté
      ctx.fillStyle = "#000000";
      ctx.fillRect(0, 0, width, height);
      
      const centerX = width / 2;
      const centerY = height / 2;
      const maxRadius = Math.min(width, height) * 0.4;
      
      // Créer le motif zone plate
      for (let radius = 1; radius < maxRadius; radius += 2) {
        const frequency = radius * radius / (maxRadius * maxRadius) * 50;
        const intensity = Math.sin(frequency) > 0 ? 255 : 0;
        
        ctx.strokeStyle = `rgb(${intensity}, ${intensity}, ${intensity})`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
        ctx.stroke();
      }
      break;
    }
    
    case "crosshatch": {
      // Crosshatch - Grille de convergence pour vidéoprojecteurs
      ctx.fillStyle = "#000000";
      ctx.fillRect(0, 0, width, height);
      
      const step = Math.max(width, height) / 20;
      
      ctx.strokeStyle = "#FFFFFF";
      ctx.lineWidth = 1;
      
      // Lignes verticales
      for (let x = 0; x <= width; x += step) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
      }
      
      // Lignes horizontales
      for (let y = 0; y <= height; y += step) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
      }
      
      // Croix centrale plus épaisse
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(width / 2, 0);
      ctx.lineTo(width / 2, height);
      ctx.moveTo(0, height / 2);
      ctx.lineTo(width, height / 2);
      ctx.stroke();
      break;
    }
    
    case "focusPattern": {
      // Focus Pattern - Motif de mise au point avec lignes fines
      ctx.fillStyle = "#808080";
      ctx.fillRect(0, 0, width, height);
      
      const centerX = width / 2;
      const centerY = height / 2;
      
      // Lignes fines radiales pour test de netteté
      ctx.strokeStyle = "#000000";
      ctx.lineWidth = 1;
      
      for (let angle = 0; angle < 360; angle += 5) {
        const radian = (angle * Math.PI) / 180;
        const length = Math.min(width, height) * 0.4;
        
        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.lineTo(
          centerX + Math.cos(radian) * length,
          centerY + Math.sin(radian) * length
        );
        ctx.stroke();
      }
      
      // Cercles concentriques
      for (let radius = 20; radius < Math.min(width, height) / 2; radius += 20) {
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
        ctx.stroke();
      }
      break;
    }
    
    case "geometricDistortion": {
      // Geometric Distortion - Test de déformation géométrique
      ctx.fillStyle = "#000000";
      ctx.fillRect(0, 0, width, height);
      
      ctx.strokeStyle = "#FFFFFF";
      ctx.lineWidth = 2;
      
      // Rectangle de bordure
      ctx.strokeRect(10, 10, width - 20, height - 20);
      
      // Grille interne
      const gridSteps = 8;
      const stepX = (width - 20) / gridSteps;
      const stepY = (height - 20) / gridSteps;
      
      ctx.lineWidth = 1;
      for (let i = 1; i < gridSteps; i++) {
        // Lignes verticales
        ctx.beginPath();
        ctx.moveTo(10 + i * stepX, 10);
        ctx.lineTo(10 + i * stepX, height - 10);
        ctx.stroke();
        
        // Lignes horizontales
        ctx.beginPath();
        ctx.moveTo(10, 10 + i * stepY);
        ctx.lineTo(width - 10, 10 + i * stepY);
        ctx.stroke();
      }
      
      // Diagonales
      ctx.strokeStyle = "#FF0000";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(10, 10);
      ctx.lineTo(width - 10, height - 10);
      ctx.moveTo(width - 10, 10);
      ctx.lineTo(10, height - 10);
      ctx.stroke();
      
      // Cercle central
      const centerX = width / 2;
      const centerY = height / 2;
      const radius = Math.min(width, height) * 0.1;
      
      ctx.strokeStyle = "#00FF00";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
      ctx.stroke();
      break;
    }
    
    case "windowPattern": {
      // Window Pattern - Fenêtres de test pour positionnement
      const windowWidth = width * 0.2;
      const windowHeight = height * 0.15;
      
      // Fond gris
      ctx.fillStyle = "#404040";
      ctx.fillRect(0, 0, width, height);
      
      // Fenêtres blanches aux 4 coins et centre
      ctx.fillStyle = "#FFFFFF";
      
      // Coin supérieur gauche
      ctx.fillRect(10, 10, windowWidth, windowHeight);
      
      // Coin supérieur droit
      ctx.fillRect(width - windowWidth - 10, 10, windowWidth, windowHeight);
      
      // Coin inférieur gauche
      ctx.fillRect(10, height - windowHeight - 10, windowWidth, windowHeight);
      
      // Coin inférieur droit
      ctx.fillRect(width - windowWidth - 10, height - windowHeight - 10, windowWidth, windowHeight);
      
      // Fenêtre centrale
      const centerX = width / 2 - windowWidth / 2;
      const centerY = height / 2 - windowHeight / 2;
      ctx.fillRect(centerX, centerY, windowWidth, windowHeight);
      
      // Texte dans les fenêtres
      ctx.fillStyle = "#000000";
      ctx.font = `${Math.max(12, windowWidth / 15)}px Arial`;
      ctx.textAlign = "center";
      
      const textY = windowHeight / 2 + 5;
      ctx.fillText("TL", windowWidth / 2 + 10, textY + 10);
      ctx.fillText("TR", width - windowWidth / 2 - 10, textY + 10);
      ctx.fillText("BL", windowWidth / 2 + 10, height - windowHeight / 2 - 10);
      ctx.fillText("BR", width - windowWidth / 2 - 10, height - windowHeight / 2 - 10);
      ctx.fillText("CENTER", width / 2, height / 2 + 5);
      break;
    }
    
    case "multiburst": {
      // Multiburst - Test de résolution horizontale (OPTIMISÉ avec ImageData)
      const centerY = height / 2;
      const burstHeight = height * 0.6;
      const startY = Math.floor(centerY - burstHeight / 2);
      const endY = Math.floor(startY + burstHeight);

      const frequencies = [0.5, 1, 2, 3.58, 4.2, 5.5];
      const sectionWidth = width / frequencies.length;

      const imageData = new ImageData(width, height);
      const data = imageData.data;

      // Remplir avec fond gris
      for (let i = 0; i < data.length; i += 4) {
        data[i] = 128;
        data[i + 1] = 128;
        data[i + 2] = 128;
        data[i + 3] = 255;
      }

      // Générer les burst patterns directement dans ImageData
      for (let i = 0; i < frequencies.length; i++) {
        const freq = frequencies[i];
        const x = Math.floor(i * sectionWidth);
        const nextX = Math.floor((i + 1) * sectionWidth);

        for (let py = startY; py < endY; py++) {
          for (let px = x; px < nextX; px++) {
            const intensity = Math.sin((px - x) * freq * 0.1) > 0 ? 255 : 0;
            const idx = (py * width + px) * 4;
            data[idx] = intensity;
            data[idx + 1] = intensity;
            data[idx + 2] = intensity;
            data[idx + 3] = 255;
          }
        }
      }

      ctx.putImageData(imageData, 0, 0);

      // Étiquettes de fréquence
      ctx.fillStyle = "#FFFFFF";
      ctx.font = `${Math.max(10, width / 100)}px Arial`;
      ctx.textAlign = "center";
      for (let i = 0; i < frequencies.length; i++) {
        const freq = frequencies[i];
        const x = i * sectionWidth;
        ctx.fillText(`${freq} MHz`, x + sectionWidth / 2, height - 20);
      }
      break;
    }
    
    case "gamma22": {
      // Gamma 2.2 Ramp - Test gamma spécifique (OPTIMISÉ)
      const steps = 16;
      const stepWidth = width / steps;

      const imageData = new ImageData(width, height);
      const data = imageData.data;

      for (let i = 0; i < steps; i++) {
        const linear = i / (steps - 1);
        const gamma22 = Math.pow(linear, 1 / 2.2);
        const gray = Math.floor(gamma22 * 255);

        const x = Math.floor(i * stepWidth);
        const nextX = Math.floor((i + 1) * stepWidth);

        for (let py = 0; py < height; py++) {
          for (let px = x; px < nextX; px++) {
            const idx = (py * width + px) * 4;
            data[idx] = gray;
            data[idx + 1] = gray;
            data[idx + 2] = gray;
            data[idx + 3] = 255;
          }
        }
      }

      ctx.putImageData(imageData, 0, 0);

      // Étiquettes
      for (let i = 0; i < steps; i++) {
        if (i % 2 === 0) {
          const linear = i / (steps - 1);
          const gamma22 = Math.pow(linear, 1 / 2.2);
          const gray = Math.floor(gamma22 * 255);
          ctx.fillStyle = gray > 127 ? "#000000" : "#FFFFFF";
          ctx.font = `${Math.max(10, width / 120)}px Arial`;
          ctx.textAlign = "center";
          ctx.fillText(
            `${Math.round(linear * 100)}%`,
            i * (width / steps) + stepWidth / 2,
            height - 10
          );
        }
      }
      break;
    }
    
    case "whiteUniformity": {
      // White Uniformity - Test d'uniformité blanc
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, width, height);
      
      // Grille de mesure avec légers contours
      const gridSize = Math.max(width, height) / 10;
      ctx.strokeStyle = "#F0F0F0";
      ctx.lineWidth = 1;
      
      for (let x = gridSize; x < width; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
      }
      
      for (let y = gridSize; y < height; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
      }
      
      // Points de mesure
      ctx.fillStyle = "#E0E0E0";
      const pointSize = 4;
      for (let x = gridSize / 2; x < width; x += gridSize) {
        for (let y = gridSize / 2; y < height; y += gridSize) {
          ctx.fillRect(x - pointSize / 2, y - pointSize / 2, pointSize, pointSize);
        }
      }
      break;
    }
    
    case "blackUniformity": {
      // Black Uniformity - Test d'uniformité noir
      ctx.fillStyle = "#000000";
      ctx.fillRect(0, 0, width, height);
      
      // Grille de mesure avec légers contours
      const gridSize = Math.max(width, height) / 10;
      ctx.strokeStyle = "#101010";
      ctx.lineWidth = 1;
      
      for (let x = gridSize; x < width; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
      }
      
      for (let y = gridSize; y < height; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
      }
      
      // Points de mesure
      ctx.fillStyle = "#202020";
      const pointSize = 4;
      for (let x = gridSize / 2; x < width; x += gridSize) {
        for (let y = gridSize / 2; y < height; y += gridSize) {
          ctx.fillRect(x - pointSize / 2, y - pointSize / 2, pointSize, pointSize);
        }
      }
      break;
    }
    
    case "ansiCheckerboard": {
      // ANSI Checkerboard - Damier ANSI pour test de contraste
      const rows = 4;
      const cols = 4;
      const cellWidth = width / cols;
      const cellHeight = height / rows;
      
      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          const isWhite = (row + col) % 2 === 0;
          ctx.fillStyle = isWhite ? "#FFFFFF" : "#000000";
          ctx.fillRect(col * cellWidth, row * cellHeight, cellWidth, cellHeight);
        }
      }
      
      // Bordure fine
      ctx.strokeStyle = "#808080";
      ctx.lineWidth = 1;
      ctx.strokeRect(0, 0, width, height);
      break;
    }
    
    case "colorTemperature": {
      // Color Temperature Test - Test température de couleur
      const sections = 4;
      const sectionWidth = width / sections;
      
      // Différentes températures de couleur
      const temperatures = [
        { temp: "3200K", color: "#FFCC99" }, // Tungstène
        { temp: "5600K", color: "#FFFFFF" }, // Lumière du jour
        { temp: "6500K", color: "#CCDDFF" }, // D65
        { temp: "9300K", color: "#9DCEFF" }  // Moniteur
      ];
      
      for (let i = 0; i < sections; i++) {
        ctx.fillStyle = temperatures[i].color;
        ctx.fillRect(i * sectionWidth, 0, sectionWidth, height);
        
        // Étiquette de température
        ctx.fillStyle = "#000000";
        ctx.font = `${Math.max(14, width / 80)}px Arial`;
        ctx.textAlign = "center";
        ctx.fillText(
          temperatures[i].temp,
          i * sectionWidth + sectionWidth / 2,
          height / 2
        );
      }
      break;
    }
    
    case "horizontalResolution": {
      // Horizontal Resolution Test
      ctx.fillStyle = "#000000";
      ctx.fillRect(0, 0, width, height);
      
      const centerY = height / 2;
      const testHeight = height * 0.3;
      const startY = centerY - testHeight / 2;
      
      // Différentes largeurs de lignes pour test résolution
      const lineWidths = [1, 2, 3, 4, 5, 6, 8, 10];
      const sectionWidth = width / lineWidths.length;
      
      for (let i = 0; i < lineWidths.length; i++) {
        const lineWidth = lineWidths[i];
        const x = i * sectionWidth;
        
        ctx.fillStyle = "#FFFFFF";
        
        // Dessiner les lignes avec la largeur spécifiée
        for (let px = x; px < x + sectionWidth; px += lineWidth * 2) {
          ctx.fillRect(px, startY, lineWidth, testHeight);
        }
        
        // Étiquette
        ctx.font = `${Math.max(10, width / 100)}px Arial`;
        ctx.textAlign = "center";
        ctx.fillText(
          `${lineWidth}px`,
          x + sectionWidth / 2,
          height - 20
        );
      }
      break;
    }
    
    case "verticalResolution": {
      // Vertical Resolution Test
      ctx.fillStyle = "#000000";
      ctx.fillRect(0, 0, width, height);
      
      const centerX = width / 2;
      const testWidth = width * 0.6;
      const startX = centerX - testWidth / 2;
      
      // Différentes hauteurs de lignes pour test résolution
      const lineHeights = [1, 2, 3, 4, 5, 6, 8, 10];
      const sectionHeight = height / lineHeights.length;
      
      for (let i = 0; i < lineHeights.length; i++) {
        const lineHeight = lineHeights[i];
        const y = i * sectionHeight;
        
        ctx.fillStyle = "#FFFFFF";
        
        // Dessiner les lignes avec la hauteur spécifiée
        for (let py = y; py < y + sectionHeight; py += lineHeight * 2) {
          ctx.fillRect(startX, py, testWidth, lineHeight);
        }
        
        // Étiquette
        ctx.font = `${Math.max(10, width / 100)}px Arial`;
        ctx.textAlign = "left";
        ctx.fillText(
          `${lineHeight}px`,
          10,
          y + sectionHeight / 2
        );
      }
      break;
    }
  }
} 